import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { championshipApi } from '../lib/api';
import { toast } from 'sonner';
import { EstadoVazio } from './common/Estados';
import { Crown, Loader2, Play, Users, FileText, Download, RefreshCw } from 'lucide-react';

/**
 * Manages the Absoluto module inside a championship.
 *
 * UX:
 *  1. Top → list of already-generated Absolute brackets (cards).
 *  2. Below → groups (age + belt + sex), each with the confirmed athletes of
 *     that demographic; staff checks who is in the Absoluto and clicks
 *     "Salvar Inscrições".
 *  3. Button "Gerar Chaves Absoluto" rebuilds the absolute brackets.
 */
export function AbsoluteTab({ championshipId, brackets, isAthlete, onBracketsChanged, modality = 'gi' }) {
  const [groups, setGroups] = useState([]);
  const [pendingChanges, setPendingChanges] = useState({}); // { athlete_id: boolean }
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const isMisto = modality === 'misto';
  const [absModality, setAbsModality] = useState('gi'); // relevante apenas no modo misto

  const fetchEligible = useCallback(async () => {
    setLoading(true);
    try {
      const params = isMisto ? { modality: absModality } : undefined;
      const { data } = await championshipApi.getAbsoluteEligible(championshipId, params);
      setGroups(data || []);
      setPendingChanges({});
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao carregar atletas');
    } finally {
      setLoading(false);
    }
  }, [championshipId, isMisto, absModality]);

  useEffect(() => {
    fetchEligible();
  }, [fetchEligible]);

  // Resolve current state of an athlete considering local pending edits.
  const isEnrolled = (athlete) => {
    if (athlete.athlete_id in pendingChanges) return pendingChanges[athlete.athlete_id];
    return athlete.enrolled_in_absolute;
  };

  const toggleAthlete = (athleteId, currentValue) => {
    setPendingChanges((prev) => {
      const next = { ...prev };
      // If new value matches the persisted one, clean it up
      const persisted = groups.flatMap((g) => g.athletes).find((a) => a.athlete_id === athleteId)?.enrolled_in_absolute;
      if (currentValue === persisted) {
        next[athleteId] = !currentValue;
      } else {
        delete next[athleteId];
      }
      return next;
    });
  };

  const dirtyCount = Object.keys(pendingChanges).length;

  const saveChanges = async () => {
    const enrollIds = Object.entries(pendingChanges).filter(([, v]) => v === true).map(([k]) => k);
    const removeIds = Object.entries(pendingChanges).filter(([, v]) => v === false).map(([k]) => k);
    if (!enrollIds.length && !removeIds.length) return;
    setSaving(true);
    try {
      const mod = isMisto ? absModality : undefined;
      if (enrollIds.length) await championshipApi.toggleAbsolute(championshipId, enrollIds, true, mod);
      if (removeIds.length) await championshipApi.toggleAbsolute(championshipId, removeIds, false, mod);
      toast.success(`Inscrições atualizadas: +${enrollIds.length} / -${removeIds.length}`);
      await fetchEligible();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handleGenerate = async () => {
    const ok = window.confirm(
      'Gerar/regerar as chaves do Absoluto?\n\n' +
      'Isso recriará as chaves de absoluto e apagará o progresso atual delas. ' +
      'As chaves de categoria de peso NÃO são afetadas.'
    );
    if (!ok) return;
    setGenerating(true);
    try {
      const { data } = await championshipApi.generateAbsoluteBrackets(championshipId);
      toast.success(data?.message || 'Chaves do Absoluto geradas');
      onBracketsChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao gerar chaves do Absoluto');
    } finally {
      setGenerating(false);
    }
  };

  const absoluteBrackets = useMemo(
    () => (brackets || []).filter((b) => b.is_absolute),
    [brackets],
  );

  const enrolledCount = useMemo(() => {
    let c = 0;
    for (const g of groups) {
      for (const a of g.athletes) {
        if (isEnrolled(a)) c += 1;
      }
    }
    return c;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups, pendingChanges]);

  if (isAthlete) {
    return (
      <Card className="bg-[#141414] border-[#27272A]">
        <CardContent>
          <EstadoVazio descricao="Esta seção é exclusiva da organização." />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="absolute-tab">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex items-center gap-3">
          <Crown className="w-6 h-6 text-[#D4AF37]" />
          <div>
            <h2 className="text-xl font-bold text-white font-['Chivo']">Módulo Absoluto</h2>
            <p className="text-sm text-[#71717A]">
              Atletas inscritos no Absoluto: <span className="text-white font-semibold">{enrolledCount}</span>
              {' · '}Chaves geradas: <span className="text-white font-semibold">{absoluteBrackets.length}</span>
            </p>
          </div>
          {isMisto && (
            <div className="flex items-center gap-2">
              <Label className="text-[#A1A1AA] text-sm">Modalidade</Label>
              <Select value={absModality} onValueChange={setAbsModality}>
                <SelectTrigger data-testid="absolute-modality" className="w-[130px] bg-[#1A1A1A] border-[#27272A] text-white h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                  <SelectItem value="gi" className="text-white hover:bg-[#27272A]">Kimono</SelectItem>
                  <SelectItem value="nogi" className="text-white hover:bg-[#27272A]">No-Gi</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchEligible}
            disabled={loading || saving}
            className="border-[#27272A] text-[#A1A1AA] hover:text-white"
            data-testid="absolute-refresh"
          >
            <RefreshCw size={16} className="mr-1" /> Recarregar
          </Button>
          {dirtyCount > 0 && (
            <Button
              onClick={saveChanges}
              disabled={saving}
              size="sm"
              className="bg-[#D4AF37] text-black hover:bg-[#E5C158]"
              data-testid="absolute-save"
            >
              {saving ? <Loader2 size={16} className="mr-1 animate-spin" /> : null}
              Salvar Inscrições ({dirtyCount})
            </Button>
          )}
          <Button
            onClick={handleGenerate}
            disabled={generating || enrolledCount < 1}
            size="sm"
            className="bg-[#D4AF37] text-black hover:bg-[#E5C158]"
            data-testid="absolute-generate"
          >
            {generating ? <Loader2 size={16} className="mr-1 animate-spin" /> : <Play size={16} className="mr-1" />}
            Gerar Chaves do Absoluto
          </Button>
        </div>
      </div>
      {isMisto && (
        <p className="text-xs text-[#71717A]">
          No evento misto o Absoluto é separado por modalidade: selecione Kimono ou No-Gi acima para
          inscrever atletas. O botão "Gerar Chaves do Absoluto" recria as chaves das duas modalidades.
        </p>
      )}

      {/* Generated absolute brackets */}
      {absoluteBrackets.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-[#A1A1AA] uppercase tracking-wider mb-2">
            Chaves do Absoluto
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {absoluteBrackets.map((b) => (
              <Card key={b._id} className="bg-[#141414] border-[#D4AF37]/30" data-testid={`absolute-bracket-${b._id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold uppercase tracking-wide text-[#D4AF37]">ABS</span>
                    <span className={`text-xs px-2 py-0.5 rounded ${b.status === 'finished' ? 'status-finished' : b.status === 'single_athlete' ? 'bg-[#D4AF37]/15 text-[#D4AF37] border border-[#D4AF37]/40' : 'status-open'}`}>
                      {b.status === 'finished' ? 'Finalizado' : b.status === 'single_athlete' ? '⚠ 1 atleta' : 'Em andamento'}
                    </span>
                  </div>
                  <div className="text-white font-medium mb-1">{b.category_name}</div>
                  <div className="text-xs text-[#A1A1AA]">{b.num_athletes} atletas · {b.rounds} rodadas</div>
                  <div className="mt-3 flex gap-1">
                    <a href={championshipApi.getBracketsPdf(championshipId, b._id)} target="_blank" rel="noopener noreferrer" className="flex-1">
                      <Button size="sm" variant="outline" className="w-full border-[#27272A] text-[#71717A] hover:text-white text-xs">
                        <Download size={12} className="mr-1" /> Geral PDF
                      </Button>
                    </a>
                    <a href={championshipApi.getStaffTemplatePdf(championshipId, b._id)} target="_blank" rel="noopener noreferrer" className="flex-1">
                      <Button size="sm" variant="outline" className="w-full border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black text-xs">
                        <FileText size={12} className="mr-1" /> Staff Chave
                      </Button>
                    </a>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Athlete enrollment matrix */}
      <div>
        <h3 className="text-sm font-semibold text-[#A1A1AA] uppercase tracking-wider mb-2">
          Inscrever Atletas no Absoluto
        </h3>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 text-[#D4AF37] animate-spin" aria-hidden="true" /><span role="status" aria-live="polite" className="ml-2 text-sm text-[#A1A1AA]">Carregando...</span>
          </div>
        ) : groups.length === 0 ? (
          <Card className="bg-[#141414] border-[#27272A]">
            <CardContent className="py-12 text-center text-[#71717A]">
              <Users className="w-10 h-10 mx-auto mb-3 opacity-50" />
              Nenhum atleta confirmado no campeonato ainda.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {groups.map((g) => {
              const enrolled = g.athletes.filter((a) => isEnrolled(a)).length;
              return (
                <Card key={g.key} className="bg-[#141414] border-[#27272A]">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <div className="text-white font-semibold">{g.display}</div>
                        <div className="text-xs text-[#71717A]">
                          {enrolled} de {g.athletes.length} atletas inscritos no Absoluto
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                      {g.athletes.map((a) => {
                        const enrolledNow = isEnrolled(a);
                        const dirty = a.athlete_id in pendingChanges;
                        return (
                          <button
                            key={a.athlete_id}
                            type="button"
                            onClick={() => toggleAthlete(a.athlete_id, enrolledNow)}
                            className={`flex items-center gap-2 p-2 rounded-lg border text-left transition-colors ${
                              enrolledNow
                                ? 'bg-[#D4AF37]/10 border-[#D4AF37]/40 hover:border-[#D4AF37]'
                                : 'bg-[#1A1A1A] border-[#27272A] hover:border-[#3F3F46]'
                            }`}
                            data-testid={`absolute-toggle-${a.athlete_id}`}
                          >
                            <div className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 ${
                              enrolledNow ? 'border-[#D4AF37] bg-[#D4AF37]' : 'border-[#52525B]'
                            }`}>
                              {enrolledNow && <div className="w-1.5 h-1.5 rounded-sm bg-black" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm text-white truncate flex items-center gap-1">
                                {a.athlete_name}
                                {dirty && <span className="text-micro text-[#D4AF37]">●</span>}
                              </div>
                              <div className="text-xs text-[#71717A] truncate">
                                {a.athlete_age_class && <span className="mr-1">{a.athlete_age_class}</span>}
                                {a.athlete_weight && <>· {a.athlete_weight} kg </>}
                                {a.weight_category && <>· {a.weight_category}</>}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default AbsoluteTab;
